const stripInlineNoteRangesForWordCount = (value: string): string => {
  if (!value) return '';

  let result = '';
  let index = 0;
  let insideNoteRange = false;

  while (index < value.length) {
    if (value[index] === '+' && value[index + 1] === '+') {
      insideNoteRange = !insideNoteRange;
      index += 2;
      continue;
    }

    if (!insideNoteRange) {
      result += value[index];
    }

    index += 1;
  }

  return result;
};

const normalizeForWordProgress = (value: string): string =>
  stripInlineNoteRangesForWordCount(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/[\r\n\t]+/g, ' ')
    .trim();

const wordSegmenter = typeof Intl !== 'undefined' && 'Segmenter' in Intl
  ? new Intl.Segmenter(undefined, { granularity: 'word' })
  : null;

const fallbackWordTokens = (value: string): string[] =>
  value.match(/[\p{L}\p{M}\p{N}]+(?:[’'][\p{L}\p{M}\p{N}]+)*/gu) ?? [];

export const getWordProgressTokens = (value: string): string[] => {
  const normalized = normalizeForWordProgress(value);
  if (!normalized) return [];
  if (!wordSegmenter) return fallbackWordTokens(normalized);

  return Array.from(wordSegmenter.segment(normalized))
    .filter((item) => item.isWordLike)
    .map((item) => item.segment);
};

export const countWordsForProgress = (value: string): number => getWordProgressTokens(value).length;

export type WordProgressDelta = {
  previousWordCount: number;
  nextWordCount: number;
  grossWords: number;
  removedWords: number;
  netWords: number;
};

type TokenEditCounts = {
  insertions: number;
  deletions: number;
  substitutions: number;
};

const MAX_TOKEN_EDIT_MATRIX_CELLS = 250_000;

const choosePreferredTokenEdit = (candidates: TokenEditCounts[]): TokenEditCounts =>
  candidates.reduce((best, candidate) => {
    const bestCost = best.insertions + best.deletions + best.substitutions;
    const candidateCost = candidate.insertions + candidate.deletions + candidate.substitutions;
    if (candidateCost !== bestCost) return candidateCost < bestCost ? candidate : best;
    if (candidate.substitutions !== best.substitutions) {
      return candidate.substitutions > best.substitutions ? candidate : best;
    }
    if (candidate.insertions !== best.insertions) {
      return candidate.insertions < best.insertions ? candidate : best;
    }
    return candidate.deletions < best.deletions ? candidate : best;
  });

const calculateTokenEditCounts = (
  previousTokens: string[],
  nextTokens: string[]
): TokenEditCounts => {
  let prefixLength = 0;
  while (
    prefixLength < previousTokens.length &&
    prefixLength < nextTokens.length &&
    previousTokens[prefixLength] === nextTokens[prefixLength]
  ) {
    prefixLength += 1;
  }

  let previousEnd = previousTokens.length;
  let nextEnd = nextTokens.length;
  while (
    previousEnd > prefixLength &&
    nextEnd > prefixLength &&
    previousTokens[previousEnd - 1] === nextTokens[nextEnd - 1]
  ) {
    previousEnd -= 1;
    nextEnd -= 1;
  }

  const previousMiddle = previousTokens.slice(prefixLength, previousEnd);
  const nextMiddle = nextTokens.slice(prefixLength, nextEnd);
  if (previousMiddle.length === 0) {
    return { insertions: nextMiddle.length, deletions: 0, substitutions: 0 };
  }
  if (nextMiddle.length === 0) {
    return { insertions: 0, deletions: previousMiddle.length, substitutions: 0 };
  }

  if (previousMiddle.length * nextMiddle.length > MAX_TOKEN_EDIT_MATRIX_CELLS) {
    const netDifference = nextMiddle.length - previousMiddle.length;
    return {
      insertions: Math.max(0, netDifference),
      deletions: Math.max(0, -netDifference),
      substitutions: Math.min(previousMiddle.length, nextMiddle.length),
    };
  }

  let previousRow: TokenEditCounts[] = Array.from(
    { length: nextMiddle.length + 1 },
    (_, index) => ({ insertions: index, deletions: 0, substitutions: 0 })
  );

  for (let previousIndex = 1; previousIndex <= previousMiddle.length; previousIndex += 1) {
    const currentRow: TokenEditCounts[] = [
      { insertions: 0, deletions: previousIndex, substitutions: 0 },
    ];

    for (let nextIndex = 1; nextIndex <= nextMiddle.length; nextIndex += 1) {
      if (previousMiddle[previousIndex - 1] === nextMiddle[nextIndex - 1]) {
        currentRow[nextIndex] = previousRow[nextIndex - 1];
        continue;
      }

      const substitutionBase = previousRow[nextIndex - 1];
      const insertionBase = currentRow[nextIndex - 1];
      const deletionBase = previousRow[nextIndex];
      currentRow[nextIndex] = choosePreferredTokenEdit([
        { ...substitutionBase, substitutions: substitutionBase.substitutions + 1 },
        { ...insertionBase, insertions: insertionBase.insertions + 1 },
        { ...deletionBase, deletions: deletionBase.deletions + 1 },
      ]);
    }

    previousRow = currentRow;
  }

  return previousRow[nextMiddle.length];
};

export const calculateWordProgressDelta = (previousText: string, nextText: string): WordProgressDelta => {
  const previousTokens = getWordProgressTokens(previousText).map((token) => token.toLocaleLowerCase());
  const nextTokens = getWordProgressTokens(nextText).map((token) => token.toLocaleLowerCase());
  const edits = calculateTokenEditCounts(previousTokens, nextTokens);

  return {
    previousWordCount: previousTokens.length,
    nextWordCount: nextTokens.length,
    grossWords: edits.insertions,
    removedWords: edits.deletions,
    netWords: nextTokens.length - previousTokens.length,
  };
};
